/**
 * انتقال بین پرسنل (v2.133.0 → ساده‌سازی v2.134.0 → ماه + ویرایش v2.135.0).
 *
 * کاربر: «یک چکِ بزرگ به یک نفر می‌دهیم که حقوقِ چند نفرِ دیگر هم داخلش است» و «خانم زهرا مرادی
 * حقوقش را باید بدهد به سمیرا تیمورزاده که بدهکار است». مبلغ از حسابِ نفرِ اول کم و به حسابِ نفرِ دوم
 * اضافه می‌شود. «بابتِ حقوقِ کدام ماه» ⇒ در لیستِ «پرداخت حقوقِ» همان ماه برای هر دو نفر دیده می‌شود
 * (کاربر: «انتقال دادم ولی در لیستِ پرداخت چیزی نیامد»). `defaults.edit_in_id` ⇒ باز شدن در حالتِ ویرایش.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, SwapOutlined } from "@ant-design/icons";
import { createTransfer, deleteTransfer, getBalances, getPeriods, getTransfers, updateTransfer } from "../api/client";
import { MoneyInput, formatMoney } from "../utils/money";
import confirmEntry from "../utils/entryCheck";
import { JALALI_MONTHS, JalaliDateInput, toJalali, todayISO } from "../utils/dateUtils";
import { apiError } from "../utils/apiError";
import { pickActivePeriod } from "../utils/activeMonth";

const { Text } = Typography;
const NO_PERIOD = 0;   // «هیچ ماهی» — فقط در حساب، نه در لیستِ پرداخت
/* v2.170.0 — «حساب دفتر» (کاربر: «صرفاً طرف‌حساب، هیچ ردی ندارد؛ همان مبدأیی که حقوق را از آن می‌دهیم»).
   سرور null می‌گیرد؛ فقط سندِ طرفِ پرسنل ساخته می‌شود. */
const OFFICE = -1;
const OFFICE_LABEL = "🏢 حساب دفتر (فقط طرف‌حساب)";
const toApi = (v) => (v === OFFICE ? null : v);
const fromApi = (v) => (v == null ? OFFICE : v);

export default function TransferModal({ open, onClose, onDone, periodId, defaults }) {
  const [form] = Form.useForm();
  const [people, setPeople] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [help, setHelp] = useState(false);
  const [editing, setEditing] = useState(null);   // ردیفِ انتقالی که ویرایش می‌شود
  const from = Form.useWatch("from_employee_id", form);
  const to = Form.useWatch("to_employee_id", form);
  const amount = Form.useWatch("amount", form);
  const pid = Form.useWatch("period_id", form);

  const reloadPeople = () => getBalances({ include_settled: true })
    .then((r) => setPeople(r.data?.rows || [])).catch(() => setPeople([]));
  const loadList = () => getTransfers().then((r) => { setList(r.data || []); return r.data || []; })
    .catch(() => { setList([]); return []; });

  const startEdit = (r) => {
    setEditing(r);
    form.setFieldsValue({ from_employee_id: fromApi(r.from_id), to_employee_id: fromApi(r.to_id), amount: r.amount,
      occurred_at: r.date, note: r.note || undefined, period_id: r.period_id || NO_PERIOD });
  };
  const resetNew = (extra = {}) => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ occurred_at: todayISO(), period_id: extra.period_id ?? NO_PERIOD, ...extra });
  };

  useEffect(() => {
    if (!open) return;
    const { edit_in_id: editId, as_salary: _ignored, ...rest } = defaults || {};
    getPeriods().then((r) => {
      const ps = r.data || [];
      setPeriods(ps);
      const def = periodId || pickActivePeriod(ps)?.id || NO_PERIOD;
      if (!editId) resetNew({ ...rest, period_id: def });
    }).catch(() => { if (!editId) resetNew(rest); });
    reloadPeople();
    loadList().then((rows) => {
      if (editId) {
        const r = rows.find((x) => x.id === editId);
        if (r) startEdit(r);
      }
    });
  }, [open]);

  const byId = useMemo(() => new Map(people.map((p) => [p.employee_id, p])), [people]);
  const opts = useMemo(() => [{ value: OFFICE, label: OFFICE_LABEL },
    ...people.map((p) => ({ value: p.employee_id, label: p.employee_name }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "fa"))], [people]);
  const periodOpts = useMemo(() => [
    ...periods.map((p) => ({ value: p.id, label: `حقوقِ ${JALALI_MONTHS[p.month - 1]} ${p.year}` })),
    { value: NO_PERIOD, label: "هیچ ماهی — فقط در حساب" },
  ], [periods]);
  const periodName = periodOpts.find((o) => o.value === pid)?.label;
  const amt = Number(amount) || 0;
  /* ماندهٔ «بدونِ همین انتقال» — در ویرایش اثرِ مبلغِ قبلی را برمی‌داریم تا پیش‌نمایش درست باشد. */
  const base = (id) => {
    if (!byId.has(id)) return null;
    let b = Number(byId.get(id).balance || 0);
    if (editing) {
      if (id === editing.from_id) b -= editing.amount;
      if (id === editing.to_id) b += editing.amount;
    }
    return b;
  };

  const submit = () => form.validateFields().then(async (v) => {
    // v2.173.0 — مبلغِ غیرعادی (صفرِ اضافه) پیش از ثبت
    if (!(await confirmEntry({ from_employee_id: toApi(v.from_employee_id), to_employee_id: toApi(v.to_employee_id),
      amount: v.amount }))) return;
    setSaving(true);
    const body = {
      from_employee_id: toApi(v.from_employee_id), to_employee_id: toApi(v.to_employee_id), amount: v.amount,
      occurred_at: v.occurred_at, note: v.note || null, period_id: v.period_id || null,
    };
    return (editing ? updateTransfer(editing.id, body) : createTransfer(body)).then(() => {
      message.success(editing ? "انتقال ویرایش شد" : "انتقال ثبت شد");
      resetNew({ period_id: v.period_id, from_employee_id: editing ? undefined : v.from_employee_id });
      loadList(); reloadPeople(); onDone && onDone();
    }).catch((e) => message.error(apiError(e))).finally(() => setSaving(false));
  });

  const nameOf = (id) => (id === OFFICE ? "حساب دفتر" : byId.get(id)?.employee_name || "");
  const known = (id) => id === OFFICE || byId.has(id);
  const state = (b) => {
    const n = Number(b) || 0;
    if (Math.abs(n) < 1) return "تسویه (نه بدهکار، نه طلبکار)";
    return n > 0 ? `${formatMoney(n)} ریال بدهکار` : `${formatMoney(-n)} ریال طلبکار`;
  };
  const ready = from && to && from !== to && amt > 0 && known(from) && known(to);
  const fromNow = ready && from !== OFFICE ? base(from) : 0;
  const toNow = ready && to !== OFFICE ? base(to) : 0;
  const fromAfter = fromNow + amt;

  return (
    <Modal open={open} onCancel={onClose} width={740}
      title={<Space><SwapOutlined />{editing ? `ویرایشِ انتقال ${editing.doc_no || ""}` : "انتقالِ پول از حسابِ یک نفر به حسابِ نفرِ دیگر"}</Space>}
      footer={[
        editing && <Button key="n" onClick={() => resetNew({ period_id: pid })}>انصراف از ویرایش</Button>,
        <Button key="c" onClick={onClose}>بستن</Button>,
        <Button key="s" type="primary" loading={saving} disabled={!ready} onClick={submit}>
          {editing ? "ذخیرهٔ ویرایش" : "ثبتِ انتقال"}</Button>,
      ]}>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        مبلغ از حسابِ نفرِ اول <b>کم</b> و به حسابِ نفرِ دوم <b>اضافه</b> می‌شود. پولی جابه‌جا نمی‌شود؛ فقط حساب‌ها اصلاح می‌شوند.
        هر طرف می‌تواند «حساب دفتر» باشد (فقط طرف‌حساب، بی‌رد).{" "}
        <Typography.Link onClick={() => setHelp((h) => !h)}>{help ? "بستنِ مثال‌ها" : "مثال ببینم"}</Typography.Link>
      </Text>
      {help && (
        <Alert type="info" style={{ marginBottom: 12 }} message={<div style={{ lineHeight: 2 }}>
          <div><b>۱) خانواده:</b> حقوقِ زهرا مرادی بدهیِ سمیرا تیمورزاده را صاف کند ⇐
            نفرِ اول: زهرا، نفرِ دوم: سمیرا، مبلغ: بدهیِ سمیرا.</div>
          <div><b>۲) یک چک برای چند نفر:</b> چکی به نامِ علی دادیم که حقوقِ رضا هم داخلش بود ⇐
            پرداختِ چک را برای علی ثبت کنید؛ بعد اینجا نفرِ اول: رضا، نفرِ دوم: علی، مبلغ: حقوقِ رضا.</div>
        </div>} />
      )}
      <Form form={form} layout="vertical">
        <Space wrap align="start" style={{ width: "100%" }}>
          <Form.Item name="from_employee_id" label="۱) از حسابِ چه کسی کم شود؟" rules={[{ required: true, message: "انتخاب کنید" }]}>
            <Select showSearch optionFilterProp="label" options={opts} style={{ width: 300 }} placeholder="کسی که پول/حقوقش را می‌دهد" />
          </Form.Item>
          <Form.Item name="to_employee_id" label="۲) به حسابِ چه کسی اضافه شود؟"
            rules={[{ required: true, message: "انتخاب کنید" }, { validator: (_, v) => (v && v === from
              ? Promise.reject(new Error("دو نفرِ متفاوت انتخاب کنید")) : Promise.resolve()) }]}>
            <Select showSearch optionFilterProp="label" options={opts} style={{ width: 300 }} placeholder="کسی که به نفعش ثبت می‌شود" />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="amount" label="۳) مبلغ (ریال)" rules={[{ required: true, message: "مبلغ؟" }]}
            extra={to && base(to) > 0 ? (
              <Typography.Link onClick={() => form.setFieldsValue({ amount: Math.round(base(to)) })}>
                به اندازهٔ کلِ بدهیِ {nameOf(to)} ({formatMoney(base(to))})</Typography.Link>) : null}>
            <MoneyInput words style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="period_id" label="۴) بابتِ حقوقِ کدام ماه؟"
            extra="در لیستِ «پرداخت حقوقِ» همین ماه برای هر دو نفر نشان داده می‌شود">
            <Select options={periodOpts} style={{ width: 220 }} />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="occurred_at" label="تاریخ" rules={[{ required: true }]}>
            <JalaliDateInput />
          </Form.Item>
          <Form.Item name="note" label="توضیح (اختیاری)">
            <Input style={{ width: 240 }} placeholder="مثلاً شمارهٔ چک" />
          </Form.Item>
        </Space>
      </Form>

      {/* نتیجه به زبانِ ساده — کاربر: «بعد از انتخاب بگوید نتیجهٔ این انتخاب چه می‌شود» */}
      {ready && (
        <Alert type={from !== OFFICE && fromAfter > 0.5 ? "warning" : "success"} showIcon message="نتیجهٔ این انتقال"
          description={<div style={{ lineHeight: 2 }}>
            {from === OFFICE
              ? <div>• <b>حساب دفتر</b>: فقط طرف‌حساب — ردی در حساب‌ها نمی‌ماند.</div>
              : <div>• <b>{nameOf(from)}</b>: الان {state(fromNow)} ← بعد: <b>{state(fromAfter)}</b></div>}
            {to === OFFICE
              ? <div>• <b>حساب دفتر</b>: فقط طرف‌حساب — ردی در حساب‌ها نمی‌ماند.</div>
              : <div>• <b>{nameOf(to)}</b>: الان {state(toNow)} ← بعد: <b>{state(toNow - amt)}</b></div>}
            {pid && from !== OFFICE ? (
              <div>• در «پرداخت حقوقِ» {periodName?.replace("حقوقِ ", "")}: از ماندهٔ پرداختِ {nameOf(from)}
                {" "}{formatMoney(amt)} ریال کم می‌شود و کنارِ نامِ هر دو نفر نوشته می‌شود.</div>
            ) : !pid ? (
              <div>• چون ماهی انتخاب نشده، فقط در حساب‌ها دیده می‌شود، نه در لیستِ پرداختِ حقوق.</div>
            ) : null}
            {from !== OFFICE && fromAfter > 0.5 && (
              <div>⚠️ {nameOf(from)} بیشتر از طلبش می‌دهد، پس بعد از این <b>بدهکار</b> می‌شود.</div>
            )}
          </div>} />
      )}
      <Typography.Title level={5} style={{ marginTop: 16 }}>انتقال‌های ثبت‌شده</Typography.Title>
      <Table size="small" rowKey="id" dataSource={list} pagination={{ pageSize: 8 }} scroll={{ x: "max-content" }}
        rowClassName={(r) => (editing && r.id === editing.id ? "ant-table-row-selected" : "")}
        columns={[
          { title: "سند", dataIndex: "doc_no", render: (v) => (v ? <code>{v}</code> : "—") },
          { title: "تاریخ", dataIndex: "date", render: (v) => toJalali(v) },
          { title: "از", dataIndex: "from_name" },
          { title: "به", dataIndex: "to_name" },
          { title: "مبلغ", dataIndex: "amount", render: (v) => formatMoney(v) },
          { title: "ماه", dataIndex: "period_id",
            render: (v) => periodOpts.find((o) => o.value === v)?.label?.replace("حقوقِ ", "") || "—" },
          { title: "", render: (_, r) => (
            <Space size={2}>
              <Button size="small" type="text" icon={<EditOutlined />} title="ویرایش" onClick={() => startEdit(r)} />
              <Popconfirm title="این انتقال حذف شود؟ هر دو حساب برمی‌گردند." okText="حذف" cancelText="خیر"
                onConfirm={() => deleteTransfer(r.id).then(() => {
                  message.success("حذف شد");
                  if (editing && editing.id === r.id) resetNew({ period_id: pid });
                  loadList(); reloadPeople(); onDone && onDone();
                }).catch((e) => message.error(apiError(e)))}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>) },
        ]} />
    </Modal>
  );
}
